import { useMemo } from 'react';
import type { AllHTMLAttributes, AriaAttributes } from 'react';

import useToggle from '../../../hooks/useToggle';

export type AccordionTargetProps = Pick<AllHTMLAttributes<HTMLDivElement> & AriaAttributes, 'id' | 'type' | 'aria-expanded' | 'aria-controls'> & {
  onClick?: () => void;
};

/*
  These are the attributes the accordion body carries. The hook supplies the id,
  role and label; className is the caller's, applied to the same element when
  they are styling the body themselves rather than using <Accordion>.
*/
export type AccordionBodyProps = Pick<AllHTMLAttributes<HTMLDivElement> & AriaAttributes, 'id' | 'role' | 'className' | 'aria-labelledby'>;

type UseAccordionProps = {
  id: string;
  isInitiallyOpen?: boolean;
};

type UseAccordionReturn = {
  isOpen: boolean;
  targetProps: AccordionTargetProps,
  bodyProps: AccordionBodyProps;
};

/**
 * Hook for managing a single accordion state and applying the proper props
 * for both the target and body. Use this if you can't use the default
 * styling provided by the main Accordion component.
 *
 * Note that this includes no styling for the items, however you can add
 * your own styling or even import the styles from Accordion.module.less and build off them.
 * You should also include your own BS `Collapse` component or use <Accordion.Body>.
 *
 * @example
 * const MyCustomAccordion = () => {
 *   const { isOpen, targetProps, bodyProps } = useAccordion({ id: 'MyCustomAccordion' });
 *   return (
 *     <div>
 *       <div {...targetProps}>
 *         {isOpen ? '⬆️ Close' : '⬇️ Open'}
 *       </div>
 *       {isOpen && (
 *         <div {...bodyProps}>
 *           Here is some body content!
 *         </div>
 *       )}
 *     </div>
 *   );
 * }
 */
const useAccordion = ({ id, isInitiallyOpen = true }: UseAccordionProps): UseAccordionReturn => {
  const [isOpen, onToggle] = useToggle(isInitiallyOpen);

  /*
    The target and the body need distinct ids so they can point at each other:
    the target controls the body, and the body is labelled by the target.
  */
  const targetId = `${id}-target`;
  const bodyId = `${id}-body`;

  const targetProps: AccordionTargetProps = useMemo(() => ({
    //  This element behaves like a button, so it should have this property.
    type: 'button',
    onClick: onToggle,
    id: targetId,
    'aria-controls': bodyId,
    'aria-expanded': isOpen,
    tabIndex: 0,
  }), [isOpen, targetId, bodyId, onToggle]);

  const bodyProps: AccordionBodyProps = useMemo(() => ({
    id: bodyId,
    role: 'region',
    'aria-labelledby': targetId,
  }), [bodyId, targetId]);

  return {
    isOpen,
    targetProps,
    bodyProps,
  };
};

export default useAccordion;
